import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useMemo, useCallback } from "react";
import * as Yup from "yup";
import { AutoComplete } from "@/core/components/ui/autocomplete";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack/stack";
import { Typography } from "@/core/components/ui/typography";
import { IBusiness } from "@/business/business";
import {
  GoogleBusinessAccount,
  useGetGoogleBusinessAccounts,
  useGetGoogleBusinessLocations,
  useGetGoogleBusinessAccountLocations,
  useCreateGoogleBusinessAccountLocation,
} from "@/business/google-business-query";
import BusinessInfoPair from "./business-info-pair";

const validationSchema = Yup.object().shape({
  selectedAccount: Yup.string().required("Please select an account"),
  selectedLocation: Yup.string().required("Please select a location"),
  selectedLocationTitle: Yup.string(),
  accountSearchValue: Yup.string(),
  locationSearchValue: Yup.string(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

interface GoogleBusinessConnectionModalProps {
  business: IBusiness;
  onClose: () => void;
}

const initialValues: FormValues = {
  selectedAccount: "",
  selectedLocation: "",
  selectedLocationTitle: "",
  accountSearchValue: "",
  locationSearchValue: "",
};

interface InnerFormProps {
  businessId: string;
  accounts: GoogleBusinessAccount[];
  accountsLoading: boolean;
  existingLocationNames: Set<string>;
  isCreating: boolean;
  onCancel: () => void;
}

const InnerForm = ({
  businessId,
  accounts,
  accountsLoading,
  existingLocationNames,
  isCreating,
  onCancel,
}: InnerFormProps) => {
  const { values, setFieldValue, isValid } = useFormikContext<FormValues>();

  const selectedAccount = useMemo(
    () => accounts.find((acc) => acc.name === values.selectedAccount),
    [accounts, values.selectedAccount],
  );

  const { data: locations = [], isLoading: locationsLoading } =
    useGetGoogleBusinessLocations(businessId, values.selectedAccount);

  const selectedLocation = useMemo(
    () => locations.find((loc) => loc.name === values.selectedLocation),
    [locations, values.selectedLocation],
  );

  const locationItems = useMemo(
    () =>
      locations
        .filter((location) => !existingLocationNames.has(location.name))
        .map((location) => ({
          value: location.name,
          label: location.storeCode
            ? `${location.title} (${location.storeCode})`
            : location.title,
        })),
    [locations, existingLocationNames],
  );

  const accountItems = useMemo(
    () =>
      accounts.map((account) => ({
        value: account.name,
        label: `${account.accountName} (${account.type})`,
      })),
    [accounts],
  );

  const handleAccountChange = useCallback(
    (value: string) => {
      setFieldValue("selectedAccount", value);
      setFieldValue("selectedLocation", "");
      setFieldValue("selectedLocationTitle", "");
      setFieldValue("locationSearchValue", "");
    },
    [setFieldValue],
  );

  const handleLocationChange = useCallback(
    (value: string) => {
      const location = locations.find((loc) => loc.name === value);
      setFieldValue("selectedLocation", value);
      setFieldValue("selectedLocationTitle", location?.title || "");
    },
    [locations, setFieldValue],
  );

  return (
    <Form>
      <Stack className="p-6 pt-0">
        <Typography variant="bodyW2">Select an account</Typography>

        <Stack fullWidth gap="sm">
          <Typography variant="subtitleW3" textColor="tertiary">
            Account name & type <span className="text-red-500">*</span>
          </Typography>
          <AutoComplete
            selectedValue={values.selectedAccount || ""}
            onSelectedValueChange={handleAccountChange}
            searchValue={values.accountSearchValue || ""}
            onSearchValueChange={(value) =>
              setFieldValue("accountSearchValue", value)
            }
            items={accountItems}
            placeholder="Select an account"
            isLoading={accountsLoading}
            fullWidth
          />
        </Stack>

        <Stack fullWidth gap="sm">
          <Typography variant="subtitleW3" textColor="tertiary">
            Location <span className="text-red-500">*</span>
          </Typography>
          <AutoComplete
            selectedValue={values.selectedLocation || ""}
            onSelectedValueChange={handleLocationChange}
            searchValue={values.locationSearchValue || ""}
            onSearchValueChange={(value) =>
              setFieldValue("locationSearchValue", value)
            }
            items={locationItems}
            placeholder="Select a location"
            isLoading={locationsLoading}
            disabled={!selectedAccount}
            fullWidth
          />
        </Stack>

        {!!selectedLocation && (
          <>
            <BusinessInfoPair
              label="Store Name"
              value={selectedLocation?.title || ""}
              isEditMode={false}
            />
            <BusinessInfoPair
              label="Store Code"
              value={selectedLocation?.storeCode || ""}
              isEditMode={false}
            />
            <BusinessInfoPair
              label="Location"
              value={selectedLocation?.name || ""}
              isEditMode={false}
            />
          </>
        )}
      </Stack>

      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onCancel()}
          disabled={isCreating}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!isValid} loading={isCreating}>
          Save
        </Button>
      </DialogFooter>
    </Form>
  );
};

export const GoogleBusinessConnectionModal = ({
  business,
  onClose,
}: GoogleBusinessConnectionModalProps) => {
  const { data: accounts = [], isLoading: accountsLoading } =
    useGetGoogleBusinessAccounts(business.id);

  const { data: existingLocations = [] } = useGetGoogleBusinessAccountLocations(
    business.id,
  );

  const { mutateAsync: createLocationMutation, isPending: isCreating } =
    useCreateGoogleBusinessAccountLocation(business.id);

  const handleSubmit = (
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>,
  ) =>
    createLocationMutation(
      {
        account: values.selectedAccount,
        location: values.selectedLocation,
        title: values.selectedLocationTitle || "",
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
        onError: (error) => {
          console.error("Failed to create Google Business location:", error);
        },
      },
    );

  const existingLocationNames = useMemo(
    () => new Set(existingLocations.map((loc) => loc.location)),
    [existingLocations],
  );

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader className="p-6">
          <DialogTitle>Connect to Google Business</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <InnerForm
            businessId={business.id}
            accounts={accounts}
            accountsLoading={accountsLoading}
            existingLocationNames={existingLocationNames}
            isCreating={isCreating}
            onCancel={onClose}
          />
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
